import type { ActorRef, ContextType } from "./DesignPackage";
import type { Page } from "./Page";

export type BackgroundRatio = "square_1_1" | "portrait_4_5" | "portrait_9_16" | "landscape_4_3";
export type BackgroundScope = "global" | "coach";
export type BackgroundSource = "generated" | "uploaded" | "stock";
export type BackgroundVisibility = "draft" | "active" | "archived";
export type BackgroundTier = "quality" | "fast";
export type BackgroundPolicy = "suggested" | "library" | "library_and_upload";

/** The closed tag vocabularies; labels are FE copy (`facetLabels.ts`). */
export const BACKGROUND_FACETS = {
  setting: [
    "home_exterior",
    "home_interior",
    "closing_and_keys",
    "moving_day",
    "neighborhood",
    "office_and_advice",
    "city_skyline",
    "nature",
    "abstract_texture",
  ],
  region: [
    "coastal",
    "tropical",
    "mountain",
    "desert",
    "southern",
    "suburban",
    "rural",
    "pacific_northwest",
    "urban",
    "none",
  ],
  occasion: [
    "none",
    "closing_day",
    "first_home",
    "home_anniversary",
    "homeownership_month",
    "back_to_school",
    "tax_season",
    "seasonal",
    "winter_holiday",
    "patriotic",
    "remembrance",
    "thanksgiving",
    "halloween",
    "new_year",
    "valentines",
    "spring_holiday",
  ],
  season: ["spring", "summer", "fall", "winter", "none"],
  timeOfDay: ["morning", "midday", "golden_hour", "night", "none"],
  mood: ["warm", "clean", "bold", "calm"],
  palette: ["light", "dark", "neutral", "colorful"],
  emptySpace: ["top", "bottom", "left", "right", "center", "none"],
} as const;

export type BackgroundFacet = keyof typeof BACKGROUND_FACETS;

export interface BackgroundAssetVersion {
  id: number;
  ratio: BackgroundRatio;
  kind: "master" | "reframe";
  status: "pending" | "ready" | "failed" | "rejected";
  url: string;
  width: number | null;
  height: number | null;
  focalX: number | null;
  focalY: number | null;
  model: string | null;
}

/** One standard size as designs show it: its own picture, or the best one cropped to it. */
export interface BackgroundAssetSize {
  ratio: BackgroundRatio;
  url: string;
  focal: string;
  compatible: boolean;
  /** made = a picture made for this size; cropped = another size's picture; making = this size's own is pending. */
  kind: "made" | "cropped" | "making";
}

export interface BackgroundAsset {
  id: number;
  coachId: number | null;
  scope: BackgroundScope;
  source: BackgroundSource;
  visibility: BackgroundVisibility;
  status: "requested" | "generating" | "ready" | "failed";
  tier: BackgroundTier | null;
  model: string | null;
  masterRatio: BackgroundRatio;
  description: string | null;
  /** The generation prompt, only for admins and the owning coach. */
  instruction?: string | null;
  setting: string | null;
  region: string | null;
  occasion: string | null;
  season: string | null;
  timeOfDay: string | null;
  mood: string | null;
  palette: string | null;
  emptySpace: string | null;
  hasPeople: boolean | null;
  hasText: boolean | null;
  hasLogo: boolean | null;
  isBusy: boolean | null;
  videoSafe: boolean | null;
  taggedAt: string | null;
  usageCount: number;
  parentAssetId: number | null;
  createdAt: string;
  createdBy: ActorRef | null;
  /** `url`, `focal`, `compatible`, and `retained` are resolved by the server for the requested ratio (or the master). */
  url: string;
  focal: string;
  compatible: boolean;
  retained: number;
  versions: BackgroundAssetVersion[];
  /** Detail responses only (`GET /background_assets/:id`). */
  sizes?: BackgroundAssetSize[];
}

export interface BackgroundRequest {
  id: number;
  kind: "generate" | "reframe" | "refine";
  status: "queued" | "running" | "done" | "failed" | "canceled";
  priority: number;
  model: string | null;
  tier: BackgroundTier | null;
  instruction?: string;
  aspectRatio: BackgroundRatio;
  designTemplateId: number | null;
  designPackageItemId: number | null;
  backgroundAssetId: number | null;
  batchKey: string | null;
  position: number;
  attempts: number;
  estimatedWaitSeconds: number | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface BackgroundSearchParams {
  scope?: "all" | "global" | "coach";
  coachId?: number;
  q?: string;
  setting?: string[];
  region?: string[];
  occasion?: string[];
  season?: string[];
  timeOfDay?: string[];
  mood?: string[];
  palette?: string[];
  emptySpace?: string[];
  hasPeople?: boolean;
  hasText?: boolean;
  hasLogo?: boolean;
  videoSafe?: boolean;
  /** Keeps only assets that fit, and resolves each row for it. */
  ratio?: BackgroundRatio;
  /** Resolves each row's url, focal, and compatible for this ratio without filtering any out. */
  previewRatio?: BackgroundRatio;
  suitableFor?: ContextType;
  /** Children of one asset (refines made from it). */
  parentAssetId?: number;
  visibility?: BackgroundVisibility[];
  source?: BackgroundSource[];
  tier?: BackgroundTier[];
  sort?: "recent" | "popular" | "relevance";
  cursor?: string;
  perPage?: number;
}

export interface BackgroundSearchBody extends BackgroundSearchParams {
  wants?: {
    setting?: string;
    mood?: string;
    season?: string;
    timeOfDay?: string;
    occasion?: string;
    peopleOk?: boolean;
  };
}

/** `matches`: rows above the matcher's minimum with `wants`, else the filtered total. */
export interface BackgroundSearchPage extends Page<BackgroundAsset> {
  matches?: number;
}

/** The server reads the image size, so no ratio is sent. */
export interface UploadBackgroundRequest {
  url: string;
  coachId?: number;
  scope?: BackgroundScope;
  visibility?: BackgroundVisibility;
}

export interface GenerateBackgroundsRequest {
  instruction?: string;
  hint?: string;
  count: number;
  ratio: BackgroundRatio;
  scope?: BackgroundScope;
  /** Admins generating into one coach's library. */
  coachId?: number;
  visibility?: BackgroundVisibility;
  designTemplateId?: number;
  bulk?: boolean;
}

export interface RefineBackgroundRequest {
  instruction: string;
  designTemplateId?: number;
  scope?: BackgroundScope;
}

export type BackgroundAssetPatch = Partial<
  Pick<
    BackgroundAsset,
    BackgroundFacet | "hasPeople" | "hasText" | "hasLogo" | "isBusy" | "videoSafe" | "description" | "visibility"
  >
>;

export interface BackgroundRequestListParams {
  scope?: "mine" | "all";
  coachId?: number;
  status?: BackgroundRequest["status"];
  designTemplateId?: number;
  batchKey?: string[];
  perPage?: number;
}

interface BackgroundModelQueue {
  rpm: number;
  enabled: boolean;
  startsInWindow: number;
  paused: boolean;
  pausedUntil: string | null;
}

/** Admin ops view of the image queue. */
export interface BackgroundsQueueStatus {
  models: { flash: BackgroundModelQueue; flashLite: BackgroundModelQueue };
  queued: { interactive: number; filler: number };
  running: number;
  oldestQueuedAt: string | null;
  provider: string;
}

export type BackgroundFacetValues = Record<BackgroundFacet, string[]>;
